import { BlockQuestionItem, ChoiceQuestion, IQuestion } from '@interfaces/questions';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
	IQuestionnaireGroup,
	IQuestionnaireItem,
	IQuestionnaireOverviewItem,
} from '@interfaces/interfaces';
import { NzJustify } from 'ng-zorro-antd/grid';
import {first, isNil, last} from 'lodash';
import { WorkflowItemDataDirective } from '@app/workflow/directives/workflow-item-data/workflow-item-data.directive';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';

@Component({
	selector: 'workflow-questionnaire-block-question',
	templateUrl: './questionnaire-block-question.component.html',
	styleUrls: ['./questionnaire-block-question.component.scss']
})
export class QuestionnaireBlockQuestionComponent implements OnChanges, OnDestroy, OnInit {

	@Input() question: IQuestionnaireItem;
	@Input() finishedAndLocked = false;
	@Input() softLocked = false;
	@Input() hasWorkflowWritePermission = false;
	@Input() validationLevel: 'error' | 'warning' = 'error';
	@Input() justify: NzJustify;
	@Input() isWizard = false;
	@Input() availableHeight: number;
	@Input() layout: 'horizontal' | 'vertical';
	@Input() hideItemText: boolean;
	@Input() modalView: boolean;
	@Input() blockMissingValueKey: string;
	@Input() set group(group: IQuestionnaireGroup & {missingValueKeyGroup?: string}) {
		this.groupMissingValueKey = group.missingValueKey || group.missingValueKeyGroup;
		this.groupOverview = group.groupOverview;
		this.groupItemAmount = group.items?.length || 0;
	}
	groupMissingValueKey: string;
	groupItemAmount = 0;
	groupOverview: IQuestionnaireOverviewItem[];

	@Output() valid: EventEmitter<boolean> = new EventEmitter();
	@Output() overwriteDescriptionText: EventEmitter<string> = new EventEmitter(null);
	hasChoiceQuestionLabels: boolean;

	questions: BlockQuestionItem;

	private readonly subscription = new Subscription();
	private readonly update$ = new EventEmitter<never>();
	private itemsValid: boolean[] = [];
	itemsEnabled: boolean[];
	blockDataKeys: string[] = [];
	blockDataWithMissingValue: boolean[] = [];

	public showOverviewTemplate = false;

	constructor(
		private workflowItemData: WorkflowItemDataDirective,
	) { }

	ngOnInit(): void {
		const pipe = this.update$.pipe(
			switchMap(() => this.buildBlockDataKeys()),
			tap(blockDataKeys => this.blockDataWithMissingValue = blockDataKeys.map(k => k.indexOf('_missingValue') !== -1)),
			tap(blockDataKeys => this.blockDataKeys = blockDataKeys),
			switchMap(() => this.getItemsEnabled$()),
		);
		this.subscription.add(pipe.subscribe(itemsEnabled => {
			this.itemsEnabled = itemsEnabled;
			// entweder alle Items sind gültig
			const allItemsValid = this.itemsValid.every((valid, index) => !!valid || this.blockDataWithMissingValue[index] !== false || !this.itemsEnabled[index]);
			// oder alle Items sind ungültig. Optionale Items werden ignoriert, da sie auch leer gültig sind
			const emptyCompletable = this.questions?.options?.isCompletableWhenEmpty && this.itemsValid.every((valid, index) => !valid || this.questions.items[index].question?.isAnswerOptional);
			const valid = allItemsValid || emptyCompletable;
			this.valid.emit(valid);
		}));
		this.update$.emit();
	}

	ngOnDestroy(): void {
		this.overwriteDescriptionText.emit(undefined);
		this.subscription.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.question) {
			this.questions = this.question as BlockQuestionItem;
			let hasChoiceQuestionLabels = false;
			for (const item of this.questions.items) {
				const question = item.question || this.questions.default;
				hasChoiceQuestionLabels ||= (!!this.firstLabel(question) || !!this.lastLabel(question));
			}

			this.hasChoiceQuestionLabels = hasChoiceQuestionLabels;
			this.update$.emit();
			this.itemsValid = [];
		}
	}

	private buildBlockDataKeys(): Observable<string[]> {
		const data$ = this.finishedAndLocked ? this.workflowItemData.itemFull$ : this.workflowItemData.item$;
		const pipe = data$.pipe(
			switchMap(data => combineLatest([
				of(data),
				!this.groupMissingValueKey ? of(false) : data?.property$(this.groupMissingValueKey).pipe(
					map(value => !!value),
				),
				!this.blockMissingValueKey ? of(false) : data?.property$(this.blockMissingValueKey).pipe(
					map(value => !!value),
				),
			])),
			tap(([, hasGroupMissingValue, hasBlockMissingValue]) => {
				this.showOverviewTemplate = this.isBlockUsingOverview(hasBlockMissingValue) || this.isGroupUsingOverview(hasGroupMissingValue);
				if (this.showOverviewTemplate) {
					this.valid.emit(true);
				}
			}),
			switchMap(([data, hasGroupMissingValue, hasBlockMissingValue]) => {
				const observables = this.questions.items.map(item => {
					const key = `${item.key}_missingValue`;
					return data?.property$(key).pipe(
						map(value => isNil(value) && !hasGroupMissingValue && !hasBlockMissingValue ? item.key : key),
					);
				});
				return combineLatest(observables);
			}),
		);
		return pipe;
	}

	private getItemsEnabled$(): Observable<boolean[]> {
		const hasShowIf = this.questions.items.some(item => item.showIf);
		if (!hasShowIf) {
			const isEnabled = this.questions.items.map(() => true);
			return of(isEnabled);
		}

		const data$ = this.finishedAndLocked ? this.workflowItemData.itemFull$ : this.workflowItemData.item$;
		const pipe = data$.pipe(
			switchMap(data => {
				const observables = this.questions.items.map(item => {
					if (item.showIf && data) {
						return data.property$(item.showIf).pipe(map(value => !!value));
					} else {
						return of(true);
					}
				});
				return combineLatest(observables);
			}),
		);
		return pipe;
	}

	private isBlockUsingOverview(hasBlockMissingValue: boolean): boolean {
		return this.questions.overview?.length && hasBlockMissingValue;
	}
	private isGroupUsingOverview(hasGroupMissingValue: boolean): boolean {
		return this.groupOverview?.length && hasGroupMissingValue;
	}

	public setValid(valid: boolean, index: number): void {
		this.itemsValid[index] = valid;
		this.update$.emit();
	}

	firstLabel(question: IQuestion): string {
		if (question?.type === 'choice') {
			const choiceQuestion = question as ChoiceQuestion;
			return first(choiceQuestion.choices)?.label;
		}
		return undefined;
	}

	lastLabel(question: IQuestion): string {
		if (question?.type === 'choice') {
			const choiceQuestion = question as ChoiceQuestion;
			return last(choiceQuestion.choices)?.label;
		}
		return undefined;
	}
}
